/**
 * Build Your Own Pack - Web Component
 * Allows customers to build custom cookie boxes by selecting flavors
 */


class BuildYourOwnPack extends HTMLElement {
  constructor() {
    super();
    this.selectedVariants = new Map();
    this.selectedSize = null;
    this.selectedBaseVariantId = null;
    this.selectedPackSize = null;
    this._lastInputWasKeyboard = false;
    this._onKeyDown = () => { this._lastInputWasKeyboard = true; };
    this._onPointerDown = () => { this._lastInputWasKeyboard = false; };
  }

  connectedCallback() {
    this.sizeOptions = this.querySelectorAll('.size-option');
    this.allVariantCards = this.querySelectorAll('.variant-card');
    this.variantCards = [];
    this.addToCartBtn = this.querySelector('.add-to-cart-btn');
    this.selectedVariantsList = this.querySelector('.selected-variants-list');
    this.flavorsCountDisplay = this.querySelector('.flavors-count');
    this.variantsContainer = this.querySelector('.variants-grid');
    this.stickyCartSummary = this.querySelector('.sticky-cart-summary');
    this.statusElement = this.querySelector('#byo-status');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    if (this.sizeOptions.length === 0 || this.allVariantCards.length === 0) {
      return;
    }

    this.initializeDefaultSize();
    this.setupEventListeners();
    this.setupStickyCartScroll();

    document.addEventListener('keydown', this._onKeyDown, { capture: true });
    document.addEventListener('pointerdown', this._onPointerDown, { capture: true });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeyDown, { capture: true });
    document.removeEventListener('pointerdown', this._onPointerDown, { capture: true });
  }

  // ==========================================
  // Accessibility Helpers (WCAG 4.1.3)
  // ==========================================

  announceToScreenReader(message) {
    if (!this.statusElement) return;
    
    // Clear and set message to ensure it's announced
    this.statusElement.textContent = '';
    requestAnimationFrame(() => {
      this.statusElement.textContent = message;
    });
  }

  // ==========================================
  // Initialization
  // ==========================================

  initializeDefaultSize() {
    const lastSizeOption = this.sizeOptions[this.sizeOptions.length - 1];
    if (lastSizeOption) {
      this.selectSize(lastSizeOption);
    }
  }

  selectSize(option) {
    this.selectedSize = parseInt(option.dataset.size);
    this.selectedBaseVariantId = option.dataset.variantId;
    this.selectedPackSize = this.selectedSize * 4;

    if (this.flavorsCountDisplay) {
      this.flavorsCountDisplay.textContent = this.selectedSize;
    }

    this.updateExplanatoryText();
    this.filterVariantsByPackSize();
    this.createCookieContainers();
    this.updateAddToCartButton();
    this.updateDisabledState();
  }

  // The merchant's own wording (with [flavor_count]/[total_cookies] tokens
  // intact) lives in data-copy-template - see step_two_copy in the section
  // schema (sections/build-your-own.liquid) - so editing it there doesn't
  // require touching this template string.
  updateExplanatoryText() {
    const explanatoryText = this.querySelector('.explanatory-text');
    const template = explanatoryText?.dataset.copyTemplate;
    if (template) {
      const totalCookies = this.selectedSize * 4;
      explanatoryText.textContent = template
        .replaceAll('[flavor_count]', this.selectedSize)
        .replaceAll('[total_cookies]', totalCookies);
    }
  }

  filterVariantsByPackSize() {
    this.allVariantCards.forEach((card) => (card.style.display = 'none'));

    this.variantCards = Array.from(this.allVariantCards).filter((card) => {
      const shouldShow = card.dataset.productType === `${this.selectedPackSize}-pack`;
      if (shouldShow) card.style.display = 'flex';
      return shouldShow;
    });

    this.setupVariantEventListeners();
  }

  // ==========================================
  // Event Listeners
  // ==========================================

  setupEventListeners() {
    this.sizeOptions.forEach((option, index) => {
      option.addEventListener('click', () => this.handleSizeSelection(option));
      
      // Keyboard support for radio button pattern (WCAG 2.1.1)
      option.addEventListener('keydown', (e) => {
        this.handleSizeKeydown(e, option, index);
      });
    });

    this.addToCartBtn?.addEventListener('click', () => this.handleAddToCart());
  }

  // Keyboard navigation for size selector (radio group pattern)
  handleSizeKeydown(e, option, index) {
    const isRtl = document.dir === 'rtl';
    let targetIndex = index;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        targetIndex = isRtl 
          ? (index - 1 + this.sizeOptions.length) % this.sizeOptions.length
          : (index + 1) % this.sizeOptions.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        targetIndex = isRtl
          ? (index + 1) % this.sizeOptions.length
          : (index - 1 + this.sizeOptions.length) % this.sizeOptions.length;
        break;
      case ' ':
      case 'Enter':
        e.preventDefault();
        this.handleSizeSelection(option);
        return;
      default:
        return;
    }

    // Move focus and select new option
    const targetOption = this.sizeOptions[targetIndex];
    this.handleSizeSelection(targetOption);
    targetOption.focus();
  }

  setupVariantEventListeners() {
    this.variantCards.forEach((card) => {
      const variantId = card.dataset.variantId;
      const decreaseBtn = card.querySelector('.quantity-decrease');
      const increaseBtn = card.querySelector('.quantity-increase');

      // Clone buttons to remove old listeners
      if (decreaseBtn) {
        const newDecreaseBtn = decreaseBtn.cloneNode(true);
        decreaseBtn.parentNode.replaceChild(newDecreaseBtn, decreaseBtn);
        newDecreaseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.updateQuantity(card, variantId, -1);
        });
      }

      if (increaseBtn) {
        const newIncreaseBtn = increaseBtn.cloneNode(true);
        increaseBtn.parentNode.replaceChild(newIncreaseBtn, increaseBtn);
        newIncreaseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.updateQuantity(card, variantId, 1);
        });
      }

      // Card click listener
      if (!card.dataset.cardClickInitialized) {
        card.dataset.cardClickInitialized = 'true';
        card.addEventListener('click', (e) => {
          if (e.target.closest('.quantity-selector, .assortment-flavor-toggle') || card.classList.contains('disabled')) return;
          this.updateQuantity(card, variantId, 1);
        });

        // Keyboard support for variant cards (WCAG 2.1.1)
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (e.target.closest('.quantity-selector, .assortment-flavor-toggle') || card.classList.contains('disabled')) return;
            e.preventDefault();
            this.updateQuantity(card, variantId, 1);
          }
        });
      }
    });
  }

  // ==========================================
  // Size Selection
  // ==========================================

  handleSizeSelection(option) {
    // Update visual and ARIA states for all options
    this.sizeOptions.forEach((opt) => {
      opt.classList.remove('selected');
      opt.setAttribute('aria-checked', 'false');
      opt.setAttribute('tabindex', '-1');
    });
    
    option.classList.add('selected');
    option.setAttribute('aria-checked', 'true');
    option.setAttribute('tabindex', '0');
    
    this.clearAllSelections();
    this.selectSize(option);
    
    // Announce selection to screen readers
    this.announceToScreenReader(`${option.textContent.trim()} selected`);
  }

  clearAllSelections() {
    this.selectedVariants.clear();

    this.allVariantCards.forEach((card) => {
      card.classList.remove('selected');
      const quantityInput = card.querySelector('.quantity-input');
      if (quantityInput) quantityInput.value = 0;
      this.updateQuantitySelectorVisibility(card, 0);
    });

    if (this.selectedVariantsList) {
      this.selectedVariantsList.innerHTML = '';
    }
  }

  // ==========================================
  // Quantity Management
  // ==========================================

  updateQuantity(card, variantId, delta) {
    if (!this.selectedSize) return;

    const quantityInput = card.querySelector('.quantity-input');
    const currentQty = parseInt(quantityInput.value);
    const newQty = currentQty + delta;
    const flavorName = this.getVariantTitle(card);

    if (newQty < 0) return;
    if (delta > 0 && this.getTotalSelectedItems() >= this.selectedSize) {
      this.announceToScreenReader(`Maximum of ${this.selectedSize} flavors reached. Remove a flavor to add more.`);
      return;
    }

    quantityInput.value = newQty;

    if (newQty === 0) {
      this.selectedVariants.delete(variantId);
      card.classList.remove('selected');
      card.setAttribute('aria-pressed', 'false');
      this.announceToScreenReader(`${flavorName} removed`);
    } else {
      this.selectedVariants.set(variantId, newQty);
      card.classList.add('selected');
      card.setAttribute('aria-pressed', 'true');
      
      const totalSelected = this.getTotalSelectedItems();
      const remaining = this.selectedSize - totalSelected;
      
      if (delta > 0) {
        if (remaining === 0) {
          this.announceToScreenReader(`${flavorName} added. Your box is complete! ${totalSelected} of ${this.selectedSize} flavors selected.`);
          if (this._lastInputWasKeyboard) {
            requestAnimationFrame(() => this.addToCartBtn?.focus());
          }
        } else {
          this.announceToScreenReader(`${flavorName} added. ${remaining} more flavor${remaining === 1 ? '' : 's'} needed.`);
        }
      }
    }

    this.updateQuantitySelectorVisibility(card, newQty);
    this.updateCookieContainers();
    this.updateAddToCartButton();
    this.updateDisabledState();
    this.updateStickyCartVisibility();
  }

  updateQuantitySelectorVisibility(card, quantity) {
    const decreaseBtn = card.querySelector('.quantity-decrease');
    const quantityInput = card.querySelector('.quantity-input');
    const quantitySelector = card.querySelector('.quantity-selector');

    const isPlusOnly = quantity === 0;

    if (decreaseBtn) decreaseBtn.style.display = isPlusOnly ? 'none' : 'flex';
    if (quantityInput) quantityInput.style.display = isPlusOnly ? 'none' : 'block';
    if (quantitySelector) quantitySelector.classList.toggle('plus-only', isPlusOnly);
  }

  getTotalSelectedItems() {
    let total = 0;
    this.selectedVariants.forEach((qty) => (total += qty));
    return total;
  }

  // ==========================================
  // Cookie Containers UI
  // ==========================================

  createCookieContainers() {
    if (!this.selectedVariantsList) return;
    this.selectedVariantsList.innerHTML = '';

    for (let i = 0; i < this.selectedSize; i++) {
      const wrapper = document.createElement('div');
      wrapper.className = 'cookie-wrapper';
      wrapper.dataset.index = i;
      wrapper.setAttribute('role', 'listitem');
      wrapper.innerHTML = `
        <div class="cookie-container" role="img" aria-label="${this.getOrdinalText(i + 1)} flavor slot, empty"></div>
        <div class="cookie-label">
          <div class="empty-flavor">${this.getOrdinalNumber(i + 1)} Flavor</div>
        </div>
      `;
      this.selectedVariantsList.appendChild(wrapper);
    }
  }

  getOrdinalText(num) {
    const ordinals = { 1: 'First', 2: 'Second', 3: 'Third' };
    return ordinals[num] || `${num}th`;
  }

  updateCookieContainers() {
    const wrappers = this.selectedVariantsList?.querySelectorAll('.cookie-wrapper');
    if (!wrappers || wrappers.length !== this.selectedSize) {
      this.createCookieContainers();
      return this.updateCookieContainers();
    }

    // Clear all containers
    wrappers.forEach((wrapper, index) => {
      const container = wrapper.querySelector('.cookie-container');
      const label = wrapper.querySelector('.cookie-label');
      if (container && label) {
        container.classList.remove('filled');
        container.innerHTML = '';
        container.setAttribute('aria-label', `${this.getOrdinalText(index + 1)} flavor slot, empty`);
        label.innerHTML = `<div class="empty-flavor">${this.getOrdinalNumber(index + 1)} Flavor</div>`;
      }
    });

    // Fill with selected cookies
    let containerIndex = 0;
    this.selectedVariants.forEach((quantity, variantId) => {
      const card = this.querySelector(`.variant-card[data-variant-id="${variantId}"]`);
      if (!card) return;

      const title = this.getVariantTitle(card);
      if (!title) return;

      for (let i = 0; i < quantity && containerIndex < this.selectedSize; i++) {
        this.fillCookieContainer(wrappers[containerIndex], card, title, variantId);
        containerIndex++;
      }
    });

    this.setupRemoveButtonListeners();
  }

  fillCookieContainer(wrapper, card, title, variantId) {
    const container = wrapper.querySelector('.cookie-container');
    const label = wrapper.querySelector('.cookie-label');
    if (!container || !label) return;

    container.classList.add('filled');
    container.setAttribute('aria-label', `${title} selected`);

    const sleeveType = (card.dataset.cookieSleeveType || 'vanilla').toLowerCase();
    let imageUrl = window.cookieSleeveImages?.[sleeveType];
    if (!imageUrl) {
      const variantImage = card.querySelector('img');
      imageUrl = variantImage?.src;
    }

    container.innerHTML = `
      ${imageUrl ? `<img src="${imageUrl}" alt="" class="variant-image" aria-hidden="true">` : ''}
      <button type="button" class="cookie-remove-btn" data-variant-id="${variantId}" aria-label="Remove ${title} from your box">×</button>
    `;
    label.innerHTML = `<div class="cookie-title">${title}</div>`;
  }

  getVariantTitle(card) {
    const titleElement = card.querySelector('.h28') || card.querySelector('h3');
    if (!titleElement) return '';
    return titleElement.childNodes[0]?.textContent?.trim() || titleElement.textContent.trim();
  }

  getOrdinalNumber(num) {
    const ordinals = { 1: '1<sup>st</sup>', 2: '2<sup>nd</sup>', 3: '3<sup>rd</sup>' };
    return ordinals[num] || `${num}<sup>th</sup>`;
  }

  setupRemoveButtonListeners() {
    this.querySelectorAll('.cookie-remove-btn').forEach((btn) => {
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      const handleRemove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = e.target.closest('.cookie-remove-btn').dataset.variantId;
        const card = this.querySelector(`.variant-card[data-variant-id="${variantId}"]`);
        if (card) this.updateQuantity(card, variantId, -1);
      };
      
      newBtn.addEventListener('click', handleRemove);
      
      // Keyboard support (WCAG 2.1.1)
      newBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleRemove(e);
        }
      });
    });
  }

  // ==========================================
  // Add To Cart Button & State
  // ==========================================

  updateAddToCartButton() {
    const { totalPrice } = this.calculatePriceAndDetails();
    const priceDisplay = this.addToCartBtn?.querySelector('.selected-price');

    if (priceDisplay) {
      priceDisplay.textContent = `$${totalPrice.toFixed(2)}`;
    }

    const isComplete = this.selectedSize && this.getTotalSelectedItems() === this.selectedSize;

    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !isComplete;
      this.addToCartBtn.setAttribute('aria-disabled', !isComplete ? 'true' : 'false');
      this.addToCartBtn.classList.toggle('active', isComplete);
    }
  }

  updateDisabledState() {
    const totalSelected = this.getTotalSelectedItems();
    const isAtMax = this.selectedSize && totalSelected >= this.selectedSize;

    this.variantCards.forEach((card) => {
      const variantId = card.dataset.variantId;
      const shouldDisable = isAtMax && !this.selectedVariants.has(variantId);
      card.classList.toggle('disabled', shouldDisable);
      
      // Update ARIA disabled state (WCAG 4.1.2)
      card.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
      
      // Update tabindex for disabled cards
      if (shouldDisable) {
        card.setAttribute('tabindex', '-1');
      } else {
        card.setAttribute('tabindex', '0');
      }
    });
  }

  // ==========================================
  // Price Calculation
  // ==========================================

  calculatePriceAndDetails() {
    let totalPrice = 0;
    const titles = [];
    const skus = [];
    const variantIds = [];

    const sizeOption = this.querySelector(`.size-option[data-variant-id="${this.selectedBaseVariantId}"]`);
    if (sizeOption) {
      totalPrice += parseFloat(sizeOption.dataset.price);
    }

    this.selectedVariants.forEach((quantity, variantId) => {
      const card = this.querySelector(`.variant-card[data-variant-id="${variantId}"]`);
      if (!card) return;

      const title = this.getVariantTitle(card);
      const sku = card.dataset.sku || '';
      const price = parseFloat(card.dataset.price || '0');

      totalPrice += price * quantity;

      for (let i = 0; i < quantity; i++) {
        if (title) titles.push(`(4) ${title}`);
        if (sku) skus.push(sku);
        variantIds.push(variantId);
      }
    });

    return { totalPrice, titles, skus, variantIds };
  }

  // ==========================================
  // Same Flavor Detection
  // ==========================================

  checkForSameFlavorSelection() {
    if (this.selectedVariants.size !== 1) return null;

    const [variantId, quantity] = Array.from(this.selectedVariants.entries())[0];
    if (quantity !== this.selectedSize) return null;

    const card = this.querySelector(`.variant-card[data-variant-id="${variantId}"]`);
    const standardPackVariantId = card?.dataset.standardPackVariantId;

    return standardPackVariantId && standardPackVariantId !== '' ? standardPackVariantId : null;
  }

  // ==========================================
  // Add To Cart
  // ==========================================

  async handleAddToCart() {
    if (this.addToCartBtn?.disabled || this.addToCartBtn?.classList.contains('loading')) return;

    if (!this.selectedBaseVariantId || !this.selectedSize || this.selectedVariants.size === 0) {
      this.showAccessibleError('Please select your cookie flavors before adding to cart.');
      return;
    }

    if (this.getTotalSelectedItems() !== this.selectedSize) {
      const remaining = this.selectedSize - this.getTotalSelectedItems();
      this.showAccessibleError(`Please select ${remaining} more flavor${remaining === 1 ? '' : 's'} to complete your box.`);
      return;
    }

    this.setLoadingState(true);
    this.announceToScreenReader('Adding items to your bag...');

    try {
      await this.validateCart();

      const standardPackVariantId = this.checkForSameFlavorSelection();
      if (standardPackVariantId) {
        await this.addStandardPackToCart(standardPackVariantId);
      } else {
        await this.addBundleToCart();
      }

      this.resetSelections();
      this.announceToScreenReader('Items successfully added to your bag.');
    } catch (error) {
      this.showAccessibleError(error.message || 'Something went wrong. Please try again.');
    } finally {
      this.setLoadingState(false);
    }
  }

  showAccessibleError(message) {
    // Announce error to screen readers
    this.announceToScreenReader(`Error: ${message}`);
    // Also show visual alert for sighted users
    alert(message);
  }

  async validateCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cartData = await response.json();

    if (cartData.items.some((item) => item.handle === 'rise-ai-giftcard')) {
      throw new Error('E-Gift Cards must be purchased separately. Please checkout or empty your cart to continue shopping.');
    }

    if (cartData.items.some((item) => item.product_title.includes('Cookie Club'))) {
      throw new Error('Levain Cookie Club subscription must be purchased separately. Please checkout or empty your cart to continue shopping.');
    }
  }

  setLoadingState(isLoading) {
    if (!this.addToCartBtn) return;
    const spinner = this.addToCartBtn.querySelector('.loading__spinner');

    this.addToCartBtn.disabled = isLoading;
    this.addToCartBtn.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    this.addToCartBtn.classList.toggle('loading', isLoading);
    this.addToCartBtn.classList.toggle('active', !isLoading);
    spinner?.classList.toggle('hidden', !isLoading);
  }

  // True when the Tin GWP is on for BYO and the selected pack size is a promo size
  // (sizes come from the Tin GWP qualifying SKU prefixes, e.g. GB12 -> 12).
  qualifiesForByoTinGWP() {
    return !!(window.TIN_GWP_BYO_ENABLED && window.TIN_GWP_BYO_VARIANT_ID
      && (window.TIN_GWP_BYO_PACK_SIZES || []).includes(this.selectedPackSize));
  }

  async addStandardPackToCart(standardPackVariantId) {
    const bundleId = this.generateBundleId();
    const items = [{ id: standardPackVariantId, quantity: 1, properties: { _bundle_id: bundleId } }];

    if (this.qualifiesForByoTinGWP()) {
      items.push({
        id: window.TIN_GWP_BYO_VARIANT_ID,
        quantity: 1,
        properties: { _gwp: 'true', _gwp_type: 'Standard Pack Tin', _bundle_id: bundleId, _sort_order: '0' },
      });
    }

    await this.submitCartItems(items, bundleId);
  }

  async addBundleToCart() {
    const bundleId = this.generateBundleId();
    const { totalPrice, titles, skus, variantIds } = this.calculatePriceAndDetails();

    const parentProperties = {
      Flavors: titles.join(', '),
      _total_price: totalPrice.toFixed(2),
      _bundle_id: bundleId,
      _is_bundle_parent: 'true',
      _sort_order: '1',
    };
    skus.forEach((sku, index) => (parentProperties[`_sku${index + 1}`] = sku));
    variantIds.forEach((id, index) => (parentProperties[`_variant_id${index + 1}`] = id));

    const items = [{ id: this.selectedBaseVariantId, quantity: 1, properties: parentProperties }];

    for (const [variantId, quantity] of this.selectedVariants.entries()) {
      items.push({
        id: variantId,
        quantity,
        properties: { 'Parent Product': 'Build Your Own Cookie Box', _hidden: true, _bundle_id: bundleId },
      });
    }

    if (this.qualifiesForByoTinGWP()) {
      items.push({
        id: window.TIN_GWP_BYO_VARIANT_ID,
        quantity: 1,
        properties: { _gwp: 'true', _gwp_type: 'Build Your Own Tin', _bundle_id: bundleId, _sort_order: '0' },
      });
    }

    await this.submitCartItems(items, bundleId);
  }

  async submitCartItems(items, bundleId = null) {
    // If this bundle includes the BYO Tin GWP, apply the discount code first so the
    // tin renders discounted in the cart drawer once sections re-render.
    if (typeof window.cartHasTinGWPProduct === 'function' && window.cartHasTinGWPProduct(items)
        && typeof window.applyTinGWPDiscountCode === 'function') {
      await window.applyTinGWPDiscountCode();
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      throw new Error('Failed to add items to cart');
    }

    // Get full cart data for UI update
    let cartData = await response.json();

    // Verify all bundle sleeves were actually added (safety net against partial adds)
    this.verifyBundleIntegrity(items, cartData);

    // Auto-add the GWP for qualifying BYO orders
    if (window.cartGWPUtils && typeof window.cartGWPUtils.autoAddGWP === 'function') {
      try {
        const sectionsToRender = this.cart && typeof this.cart.getSectionsToRender === 'function'
          ? this.cart.getSectionsToRender().map((section) => section.id || section.section).filter(Boolean)
          : [];
        const updatedState = await window.cartGWPUtils.autoAddGWP(cartData, {
          sections: sectionsToRender,
          sectionsUrl: window.location.pathname
        });
        if (updatedState && Array.isArray(updatedState.items)) {
          cartData = updatedState;
        }
      } catch (e) {
        console.error('[BYO] GWP auto-add error:', e);
      }
    }

    // Determine if tin upsell will be shown -- never when the box already gets a free tin
    const tinUpsellConfig = bundleId && window.BYO_TIN_UPSELL && !this.qualifiesForByoTinGWP()
      ? window.BYO_TIN_UPSELL[this.selectedPackSize]
      : null;
    const willShowTinUpsell = !!(tinUpsellConfig && tinUpsellConfig.variantId);

    if (willShowTinUpsell) {
      // Store config so updateCartUI can pre-configure the panel in incoming HTML before swap
      this._pendingByoTinUpsell = { tinConfig: tinUpsellConfig, bundleId };
      // Pre-activate on old DOM so panel is already visible when cart drawer opens
      this.showByoTinUpsell(tinUpsellConfig, bundleId);
    }

    // Open and update cart drawer
    if (this.cart && typeof this.cart.renderContents === 'function') {
      this.cart.renderContents(cartData);
    }

    // Update cart UI sections (this replaces the DOM)
    await this.updateCartUI();

    this._pendingByoTinUpsell = null;

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'build-your-own', cartData });
  }

  verifyBundleIntegrity(submittedItems, cartData) {
    // Only applies to BYO bundles — standard packs have no sleeve tracking
    const parentItem = submittedItems.find((item) => item.properties?._is_bundle_parent === 'true');
    if (!parentItem) return;

    const bundleId = parentItem.properties._bundle_id;

    // Build expected sleeve variant IDs from what was submitted
    const expectedSleeveIds = [];
    submittedItems.forEach((item) => {
      if (item.properties?._hidden) {
        for (let i = 0; i < item.quantity; i++) {
          expectedSleeveIds.push(String(item.id));
        }
      }
    });

    // Build actual sleeve variant IDs from the add response
    const addedSleeveIds = [];
    (cartData.items || []).forEach((item) => {
      if (item.properties?._bundle_id === bundleId && item.properties?._hidden) {
        for (let i = 0; i < item.quantity; i++) {
          addedSleeveIds.push(String(item.variant_id));
        }
      }
    });

    // Find missing sleeves
    const remaining = [...expectedSleeveIds];
    addedSleeveIds.forEach((id) => {
      const idx = remaining.indexOf(id);
      if (idx > -1) remaining.splice(idx, 1);
    });

    if (remaining.length > 0) {
      throw new Error('Some cookie flavors are currently unavailable. Please try again with a different selection.');
    }
  }

  async updateCartUI() {
    if (this.cart) {
      this.cart.classList.remove('is-empty');
    }

    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const targetCartDrawer = document.querySelector('#CartDrawer');
    const sourceCartDrawer = html.querySelector('#CartDrawer');
    const targetInner = document.querySelector('.drawer__inner');
    const sourceInner = html.querySelector('.drawer__inner');

    // If BYO tin upsell is pending, configure it in the incoming HTML before insertion
    if (sourceCartDrawer && this._pendingByoTinUpsell) {
      const { tinConfig, bundleId } = this._pendingByoTinUpsell;
      const newUpsellSection = sourceCartDrawer.querySelector('#CartUpsellSection');
      if (newUpsellSection) {
        this.configureByoTinUpsellPanel(newUpsellSection, tinConfig, bundleId);
      }
    }

    // Capture old fill width; save new Liquid-rendered target before overwriting.
    const oldFill = targetInner?.querySelector('.free-shipping-progress__fill');
    const prevWidth = oldFill ? oldFill.style.width : null;
    let targetWidth = null;
    if (prevWidth !== null && sourceInner) {
      const newFill = sourceInner.querySelector('.free-shipping-progress__fill');
      if (newFill) {
        targetWidth = newFill.style.width;
        newFill.style.transition = 'none';
        newFill.style.width = prevWidth;
      }
    }

    // Replace the entire #CartDrawer content so that elements outside .drawer__inner
    // (gift-message-options, cart-free-gift-message, upsell panels) are
    // also updated. When the cart transitions from empty → non-empty those elements do
    // not exist in the live DOM yet; replacing only .drawer__inner leaves them absent,
    // breaking the gift-note and upsell panel triggers.
    if (targetCartDrawer && sourceCartDrawer) {
      targetCartDrawer.innerHTML = sourceCartDrawer.innerHTML;

      // Re-query from live DOM after innerHTML swap for the animation target.
      const newFill = targetCartDrawer.querySelector('.free-shipping-progress__fill');
      if (newFill && targetWidth !== null) {
        void newFill.offsetWidth;
        newFill.style.transition = '';
        newFill.style.width = targetWidth;
      }
    }

    if (typeof initCardIsleWidget === 'function') initCardIsleWidget();
  }

  configureByoTinUpsellPanel(upsellSection, tinConfig, bundleId) {
    upsellSection.querySelectorAll('.PK-4, .PK-8, .PK-12, .NEW-PARENTS').forEach(el => {
      el.classList.add('hidden');
      el.setAttribute('aria-hidden', 'true');
    });
    upsellSection.querySelectorAll('.upsell-variant-price:not(.BYO-TIN)').forEach(el => el.classList.add('hidden'));

    const heading = upsellSection.querySelector('.upsell-title.BYO-TIN');
    if (heading) {
      heading.textContent = tinConfig.title || 'Add to your order!';
      heading.classList.remove('hidden');
      heading.removeAttribute('aria-hidden');
    }

    const byoTinDiv = upsellSection.querySelector('.byo-tin-upsell');
    if (byoTinDiv) {
      const img = byoTinDiv.querySelector('.byo-tin-upsell__img');
      const copy = byoTinDiv.querySelector('.byo-tin-upsell__copy');
      if (img) {
        img.src = tinConfig.image || '';
        img.style.display = tinConfig.image ? '' : 'none';
      }
      if (copy) copy.textContent = tinConfig.copy || '';
      byoTinDiv.classList.remove('hidden');
    }

    const priceSpan = upsellSection.querySelector('.upsell-variant-price.BYO-TIN');
    if (priceSpan && tinConfig.price != null) {
      priceSpan.textContent = ` +$${(tinConfig.price / 100).toFixed(2)}`;
      priceSpan.classList.remove('hidden');
    }

    const typeInput = upsellSection.querySelector('input[name="Upsell Type"]');
    const variantInput = upsellSection.querySelector('input[name="Upsell Variant Id"]');
    const bundleIdInput = upsellSection.querySelector('input[name="Upsell Bundle Id"]');
    if (typeInput) typeInput.value = 'byo-tin';
    if (variantInput) variantInput.value = tinConfig.variantId;
    if (bundleIdInput) bundleIdInput.value = bundleId;

    upsellSection.classList.add('active');
    upsellSection.setAttribute('aria-hidden', 'false');
  }

  showByoTinUpsell(tinConfig, bundleId) {
    const upsellSection = document.querySelector('#CartUpsellSection');
    if (!upsellSection) return;

    this.configureByoTinUpsellPanel(upsellSection, tinConfig, bundleId);

    const liveRegion = upsellSection.querySelector('#CartUpsellAnnouncement');
    if (liveRegion) liveRegion.textContent = tinConfig.title || 'Add to your order!';
  }

  generateBundleId() {
    return 'bundle_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  resetSelections() {
    this.selectedVariants.clear();
    this.variantCards.forEach((card) => {
      card.classList.remove('selected');
      const input = card.querySelector('.quantity-input');
      if (input) input.value = 0;
      this.updateQuantitySelectorVisibility(card, 0);
    });
    this.createCookieContainers();
    this.updateAddToCartButton();
    this.updateDisabledState();
  }

  // ==========================================
  // Sticky Cart
  // ==========================================

  setupStickyCartScroll() {
    if (!this.stickyCartSummary) return;

    let ticking = false;
    const checkScroll = () => {
      this.updateStickyCartVisibility();
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(checkScroll);
        ticking = true;
      }
    });

    checkScroll();
  }

  updateStickyCartVisibility() {
    if (!this.stickyCartSummary) return;

    if (this.getTotalSelectedItems() > 0) {
      this.stickyCartSummary.classList.add('visible');
      return;
    }

    const rect = this.getBoundingClientRect();
    const triggerPoint = window.innerHeight * -0.2;
    this.stickyCartSummary.classList.toggle('visible', rect.top <= triggerPoint);
  }
}

customElements.define('build-your-own-pack', BuildYourOwnPack);
